import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:     '#0A1628',
  gold:     '#C9A84C',
  black:    '#0A0A0A',
  white:    '#FFFFFF',
  surface:  '#F5F6F8',
  surfaceAlt:'#ECEEF2',
  border:   '#D0D5DD',
  borderLt: '#E8EBF0',
  text:     '#0A0A0A',
  textSec:  '#3D4552',
  muted:    '#6B7280',
  mutedLt:  '#9CA3AF',
  success:  '#0A7C42',
  danger:   '#C0222A',
};

interface ManagerFirm {
  id: string;
  name: string;
  description: string;
  headquarters: string;
  type: string;
  aum_min: number;
  aum_max: number;
  website: string;
  owner_id: string;
}

interface Fund {
  id: string;
  name: string;
  asset_class: string;
  strategy: string;
  aum_min: number;
  aum_max: number;
  minimum_subscription: number;
  management_fee: number;
  performance_fee: number;
  inception_date: string;
  status: string;
}

interface FirmWithFunds extends ManagerFirm {
  funds: Fund[];
  connectionStatus: 'none' | 'pending' | 'approved';
}

const assetClasses = [
  { value: 'all', label: 'All' },
  { value: 'hedge_fund', label: 'Hedge Funds' },
  { value: 'pe', label: 'Private Equity' },
  { value: 'vc', label: 'Venture Capital' },
  { value: 're', label: 'Real Estate' },
  { value: 'credit', label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
];

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund:     { color: '#0D6E8A', bg: '#EFF8FB' },
  pe:             { color: '#92640A', bg: '#FDF6E7' },
  vc:             { color: '#1D4ED8', bg: '#EFF4FF' },
  re:             { color: '#B91C1C', bg: '#FEF2F2' },
  credit:         { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

function maskAUM(min: number, max: number): string {
  if (!min) return 'Undisclosed';
  const avg = (min + max) / 2;
  if (avg < 100) return 'Under $100mm';
  if (avg < 500) return '$100mm – $500mm';
  if (avg < 1000) return '$500mm – $1bn';
  if (avg < 5000) return '$1bn – $5bn';
  return '$5bn+';
}

function formatAUM(min: number, max: number): string {
  if (!min && !max) return 'N/A';
  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}bn` : `$${n}mm`;
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  return fmt(min || max);
}

// ── Shared card style ─────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: '4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function AltConnect() {
  const navigate = useNavigate();
  const { firm, profile } = useProfile();
  const [firms, setFirms] = useState<FirmWithFunds[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeClass, setActiveClass] = useState('all');
  const [requesting, setRequesting] = useState<string | null>(null);
  const [compareFunds, setCompareFunds] = useState<{ id: string; name: string; asset_class: string }[]>([]);

  useEffect(() => {
    if (!firm) return;
    fetchFirms();
  }, [firm]);

  const fetchFirms = async () => {
    if (!firm) return;
    const { data: fundsData } = await supabase
      .from('funds')
      .select(`id, name, asset_class, strategy, aum_min, aum_max,
        minimum_subscription, management_fee, performance_fee,
        inception_date, status,
        firm:firms(id, name, description, headquarters, type, aum_min, aum_max, website, owner_id)`)
      .eq('status', 'open');

    if (!fundsData) { setLoading(false); return; }

    const { data: connectionsData } = await supabase
      .from('connections').select('manager_firm_id, status')
      .eq('allocator_firm_id', firm.id);

    const connectionMap: Record<string, 'pending' | 'approved'> = {};
    (connectionsData || []).forEach(c => { connectionMap[c.manager_firm_id] = c.status; });

    const firmMap: Record<string, FirmWithFunds> = {};
    fundsData.forEach((fund: any) => {
      const firmData = Array.isArray(fund.firm) ? fund.firm[0] : fund.firm;
      if (!firmData || firmData.id === firm.id) return;
      if (!firmMap[firmData.id]) {
        firmMap[firmData.id] = { ...firmData, funds: [], connectionStatus: connectionMap[firmData.id] || 'none' };
      }
      firmMap[firmData.id].funds.push({
        id: fund.id, name: fund.name, asset_class: fund.asset_class,
        strategy: fund.strategy, aum_min: fund.aum_min, aum_max: fund.aum_max,
        minimum_subscription: fund.minimum_subscription, management_fee: fund.management_fee,
        performance_fee: fund.performance_fee, inception_date: fund.inception_date, status: fund.status,
      });
    });
    setFirms(Object.values(firmMap));
    setLoading(false);
  };

  const handleConnect = async (managerFirm: FirmWithFunds) => {
    if (!firm || !profile) return;
    setRequesting(managerFirm.id);
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) { setRequesting(null); return; }

    const { data: newConn, error } = await supabase.from('connections').insert({
      allocator_firm_id: firm.id, manager_firm_id: managerFirm.id,
      fund_id: null, requested_by: user.id, status: 'pending',
    }).select('id').single();
    if (error) { console.error(error); setRequesting(null); return; }

    logActivity(user.id, firm.id, 'connection_requested', 'connection', newConn?.id || managerFirm.id, {
      manager_firm_id: managerFirm.id, manager_firm_name: managerFirm.name,
    });

    setFirms(prev => prev.map(f => f.id === managerFirm.id ? { ...f, connectionStatus: 'pending' } : f));

    try {
      const { data: ownerProfile } = await supabase
        .from('profiles').select('full_name, email').eq('id', managerFirm.owner_id).single();
      if (ownerProfile) {
        await supabase.functions.invoke('send-email-notification', {
          body: {
            type: 'fund_access_request',
            data: {
              manager_email: ownerProfile.email,
              manager_name: ownerProfile.full_name || 'there',
              allocator_firm_name: firm.name,
              allocator_name: profile.full_name || 'An allocator',
              fund_name: `all funds at ${managerFirm.name}`,
            },
          },
        });
      }
    } catch (err) { console.error('Email failed:', err); }
    setRequesting(null);
  };

  const toggleCompare = (fund: { id: string; name: string; asset_class: string }, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompareFunds(prev => {
      const exists = prev.find(f => f.id === fund.id);
      if (exists) return prev.filter(f => f.id !== fund.id);
      if (prev.length >= 4) return prev;
      return [...prev, fund];
    });
  };

  const isInCompare = (id: string) => compareFunds.some(f => f.id === id);

  const filteredFirms = firms.filter(f => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.funds.some(fund => fund.name.toLowerCase().includes(search.toLowerCase()));
    const matchClass = activeClass === 'all' || f.funds.some(fund => fund.asset_class === activeClass);
    return matchSearch && matchClass;
  });

  return (
    <div style={{
      padding: '28px 32px',
      background: C.surface, minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      paddingBottom: compareFunds.length > 0 ? '88px' : '28px',
    }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: '24px', color: C.black,
          fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px',
        }}>
          AltConnect
        </h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
          Discover and connect with alternative investment managers
        </p>
      </div>

      {/* ── Search + Filters ── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.mutedLt} strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search managers or funds..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', background: C.white,
              border: `1px solid ${C.border}`, borderRadius: '3px',
              color: C.text, fontSize: '13px',
              padding: '8px 12px 8px 32px',
              outline: 'none', fontFamily: 'system-ui',
              boxSizing: 'border-box',
            }}
            onFocus={e => (e.target.style.borderColor = C.navy)}
            onBlur={e => (e.target.style.borderColor = C.border)}
          />
        </div>
        <div style={{ fontSize: '12px', color: C.muted }}>
          {filteredFirms.length} firm{filteredFirms.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Asset class tabs ── */}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {assetClasses.map(ac => (
          <button key={ac.value} onClick={() => setActiveClass(ac.value)} style={{
            padding: '4px 10px', borderRadius: '3px',
            fontSize: '11px', cursor: 'pointer',
            fontFamily: 'system-ui', fontWeight: 500,
            border: `1px solid ${activeClass === ac.value ? C.navy : C.border}`,
            background: activeClass === ac.value ? C.navy : C.white,
            color: activeClass === ac.value ? C.white : C.muted,
            transition: 'all 0.1s', letterSpacing: '0.2px',
          }}>{ac.label}</button>
        ))}
      </div>

      {/* ── Firm list ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.muted, fontSize: '13px' }}>
          Loading managers...
        </div>
      ) : filteredFirms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.muted, fontSize: '13px' }}>
          No managers found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredFirms.map(managerFirm => {
            const isConnected = managerFirm.connectionStatus === 'approved';
            const isPending   = managerFirm.connectionStatus === 'pending';
            const isReq       = requesting === managerFirm.id;

            return (
              <div key={managerFirm.id} style={{
                ...card,
                borderLeft: isConnected ? `3px solid ${C.success}` : isPending ? `3px solid ${C.gold}` : `3px solid transparent`,
              }}>
                {/* Firm header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', padding: '16px 18px',
                  borderBottom: `1px solid ${C.borderLt}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      onClick={() => navigate(`/alt-connect/firm/${managerFirm.id}`)}
                      style={{
                        width: '38px', height: '38px', borderRadius: '3px',
                        background: C.navy,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '15px', fontWeight: 700, color: C.gold,
                        flexShrink: 0, cursor: 'pointer',
                        fontFamily: 'Georgia, serif',
                      }}
                    >
                      {managerFirm.name.charAt(0)}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          onClick={() => navigate(`/alt-connect/firm/${managerFirm.id}`)}
                          style={{
                            fontSize: '14px', fontWeight: 600, color: C.navy,
                            cursor: 'pointer', fontFamily: 'system-ui',
                          }}
                        >
                          {managerFirm.name}
                        </span>
                        {isConnected && (
                          <span style={{
                            fontSize: '10px', color: C.success, fontWeight: 700,
                            letterSpacing: '0.5px', textTransform: 'uppercase',
                          }}>
                            ✓ Connected
                          </span>
                        )}
                        {isPending && (
                          <span style={{
                            fontSize: '10px', color: C.gold, fontWeight: 700,
                            letterSpacing: '0.5px', textTransform: 'uppercase',
                          }}>
                            Pending
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                        {managerFirm.headquarters || '-'}
                        {managerFirm.aum_min
                          ? ` · ${isConnected
                            ? formatAUM(managerFirm.aum_min, managerFirm.aum_max)
                            : maskAUM(managerFirm.aum_min, managerFirm.aum_max)} AUM`
                          : ''}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                    <button
                      onClick={() => navigate(`/alt-connect/firm/${managerFirm.id}`)}
                      style={{
                        padding: '6px 14px', borderRadius: '3px',
                        fontSize: '12px', fontWeight: 500,
                        cursor: 'pointer', fontFamily: 'system-ui',
                        border: `1px solid ${C.border}`,
                        background: C.white, color: C.textSec,
                        letterSpacing: '0.2px',
                      }}
                    >
                      View Profile
                    </button>
                    {!isConnected && !isPending ? (
                      <button
                        onClick={() => handleConnect(managerFirm)}
                        disabled={isReq}
                        style={{
                          padding: '6px 14px', borderRadius: '3px',
                          fontSize: '12px', fontWeight: 600,
                          cursor: isReq ? 'not-allowed' : 'pointer',
                          border: `1px solid ${C.navy}`,
                          background: C.navy, color: C.white,
                          fontFamily: 'system-ui', letterSpacing: '0.2px',
                          opacity: isReq ? 0.7 : 1,
                        }}
                      >
                        {isReq ? 'Requesting...' : 'Request Access'}
                      </button>
                    ) : isPending ? (
                      <div style={{
                        padding: '6px 14px', borderRadius: '3px',
                        fontSize: '12px', fontWeight: 500,
                        border: `1px solid ${C.gold}`,
                        background: '#FFFBEB', color: '#92640A',
                        fontFamily: 'system-ui',
                      }}>
                        Request Sent
                      </div>
                    ) : (
                      <div style={{
                        padding: '6px 14px', borderRadius: '3px',
                        fontSize: '12px', fontWeight: 600,
                        border: `1px solid ${C.success}`,
                        background: '#F0FDF4', color: C.success,
                        fontFamily: 'system-ui',
                      }}>
                        ✓ Connected
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {managerFirm.description && (
                  <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.borderLt}` }}>
                    <p style={{ fontSize: '12px', color: C.textSec, lineHeight: 1.6, margin: 0 }}>
                      {managerFirm.description}
                    </p>
                  </div>
                )}

                {/* Funds */}
                <div style={{ padding: '12px 18px' }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 700, color: C.muted,
                    letterSpacing: '1px', textTransform: 'uppercase',
                    marginBottom: '10px', fontFamily: 'system-ui',
                  }}>
                    {managerFirm.funds.length} Fund{managerFirm.funds.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
                    {managerFirm.funds.map(fund => {
                      const cls = classColors[fund.asset_class] || { color: C.muted, bg: C.surface };
                      const inCompare = isInCompare(fund.id);
                      return (
                        <div
                          key={fund.id}
                          onClick={() => navigate(`/alt-connect/fund/${fund.id}`)}
                          style={{
                            background: inCompare ? '#EFF4FF' : C.surface,
                            border: `1px solid ${inCompare ? '#2563EB' : C.border}`,
                            borderRadius: '3px', padding: '10px 12px',
                            cursor: 'pointer', transition: 'border-color 0.1s',
                          }}
                          onMouseEnter={e => {
                            if (!inCompare) (e.currentTarget as HTMLDivElement).style.borderColor = C.navy;
                          }}
                          onMouseLeave={e => {
                            if (!inCompare) (e.currentTarget as HTMLDivElement).style.borderColor = C.border;
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 600, color: C.text, flex: 1, marginRight: '6px', lineHeight: 1.3 }}>
                              {fund.name}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                              <span style={{
                                fontSize: '9px', fontWeight: 700,
                                padding: '2px 5px', borderRadius: '2px',
                                background: cls.bg, color: cls.color,
                                letterSpacing: '0.4px', textTransform: 'uppercase',
                                fontFamily: 'system-ui',
                              }}>
                                {classLabels[fund.asset_class] || fund.asset_class}
                              </span>
                              <button
                                onClick={e => toggleCompare({ id: fund.id, name: fund.name, asset_class: fund.asset_class }, e)}
                                title={inCompare ? 'Remove from compare' : 'Add to compare'}
                                style={{
                                  background: inCompare ? '#DBEAFE' : C.white,
                                  border: `1px solid ${inCompare ? '#2563EB' : C.border}`,
                                  borderRadius: '2px',
                                  color: inCompare ? '#1D4ED8' : C.muted,
                                  fontSize: '10px', padding: '1px 5px',
                                  cursor: 'pointer', fontFamily: 'system-ui',
                                  lineHeight: 1.4, fontWeight: 600,
                                }}
                              >
                                {inCompare ? '✓' : '+'}
                              </button>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                            {[
                              { label: 'AUM', value: isConnected ? formatAUM(fund.aum_min, fund.aum_max) : maskAUM(fund.aum_min, fund.aum_max) },
                              { label: 'Strategy', value: fund.strategy || '-' },
                              { label: 'Min Sub', value: fund.minimum_subscription ? `$${fund.minimum_subscription}mm` : '-' },
                            ].map(({ label, value }) => (
                              <div key={label}>
                                <div style={{ fontSize: '9px', fontWeight: 600, color: C.mutedLt, letterSpacing: '0.5px', textTransform: 'uppercase', fontFamily: 'system-ui' }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 500, color: C.text, marginTop: '2px', fontFamily: '"Courier New", monospace' }}>
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Compare bar ── */}
      {compareFunds.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: '232px', right: 0,
          background: C.navy, borderTop: `2px solid ${C.gold}`,
          padding: '10px 28px',
          display: 'flex', alignItems: 'center', gap: '12px',
          zIndex: 500,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
        }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', flexShrink: 0, fontFamily: 'system-ui', fontWeight: 600, letterSpacing: '0.5px' }}>
            COMPARING {compareFunds.length}/4
          </div>
          <div style={{ display: 'flex', gap: '6px', flex: 1, flexWrap: 'wrap' }}>
            {compareFunds.map(f => {
              const cls = classColors[f.asset_class] || { color: C.muted, bg: C.surface };
              return (
                <div key={f.id} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '3px', padding: '4px 10px',
                }}>
                  <span style={{ fontSize: '12px', color: C.white, fontFamily: 'system-ui' }}>{f.name}</span>
                  <button
                    onClick={() => setCompareFunds(prev => prev.filter(c => c.id !== f.id))}
                    style={{
                      background: 'transparent', border: 'none',
                      color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
                      fontSize: '14px', padding: '0', lineHeight: 1,
                    }}
                  >×</button>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => navigate(`/compare?funds=${compareFunds.map(f => f.id).join(',')}`)}
            style={{
              background: C.gold, border: 'none', borderRadius: '3px',
              color: C.navy, fontSize: '12px', fontWeight: 700,
              padding: '8px 20px', cursor: 'pointer',
              fontFamily: 'system-ui', flexShrink: 0,
              letterSpacing: '0.3px',
            }}
          >
            Compare →
          </button>
          <button
            onClick={() => setCompareFunds([])}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '3px', color: 'rgba(255,255,255,0.6)',
              fontSize: '12px', padding: '7px 14px',
              cursor: 'pointer', fontFamily: 'system-ui', flexShrink: 0,
            }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}